import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "./App.css";

import ListProducts from "./ListProducts";
import { ProductPage } from "./Product";
import CheckoutPage from "./Checkout";
import Prescriptions from "./pharmacy/Prescriptions";
import ManagePage from "./pharmacy/Manage";
import ProviderLogin from './ProviderLogin';
import PharmacyLogin from './PharmacyLogin';
import Header from './Header';
import TabbedProfile from "./patient/TabbedProfile";
import ProviderOrderHistory from "./provider/ProviderOrderHistory";
import ProviderOrderHistoryDetail from "./provider/ProviderOrderHistoryDetail";
import ChangePasswordForm from "./patient/ChangePasswordForm";
import ProviderRequestForm from "./ProviderRequestForm";
import ContactSupport from "./provider/ContactSupport";
import ReorderConfirmation from "./provider/ReorderConfirmation";
import PharmacyProfile from "./pharmacy/PharmacyProfile";

// Define the theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#001e45"
    },
    secondary: {
      main: "#ea3429"
    }
  }
});

const AppContent: React.FC<{
  onLogout: () => void;
  isProviderLoggedIn: boolean;
  isPharmacyLoggedIn: boolean;
  mustChangePassword: boolean;
  pharmacistMustChangePassword: boolean;
  pharmacistPermissionLevel: string;
  setIsProviderLoggedIn: (value: boolean) => void;
  setIsPharmacyLoggedIn: (value: boolean) => void;
  setMustChangePassword: (value: boolean) => void;
  setPharmacistMustChangePassword: (value: boolean) => void;
  setPharmacistPermissionLevel: (value: string) => void;
}> = ({ onLogout, isProviderLoggedIn, isPharmacyLoggedIn, mustChangePassword, pharmacistMustChangePassword, pharmacistPermissionLevel, setIsProviderLoggedIn, setIsPharmacyLoggedIn, setMustChangePassword, setPharmacistMustChangePassword, setPharmacistPermissionLevel }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Determine if the current route is for the pharmacy
  const isPharmacyRoute = location.pathname.startsWith("/pharmacy") || location.pathname.startsWith("/manage");

  const showAdmin = searchParams.get("admin") === "true";

  const handleAdminToggle = () => {
    if (showAdmin) {
      navigate("/pharmacy");
    } else {
      setSearchParams({ admin: "true" });
    }
  };

  const handleOrdersClick = () => {
    navigate("/pharmacy");
  };

  const ProviderProtectedRoute = ({ element }: { element: JSX.Element }) => {
    if (!isProviderLoggedIn) return <Navigate to="/login" replace />;
    if (mustChangePassword && location.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
    return element;
  };

  const PharmacyProtectedRoute = ({ element }: { element: JSX.Element }) => {
    if (!isPharmacyLoggedIn) return <Navigate to="/pharmacy-login" replace />;
    if (pharmacistMustChangePassword && location.pathname !== '/pharmacy-change-password') return <Navigate to="/pharmacy-change-password" replace />;
    return element;
  };

  return (
    <>
      <Header
        onLogout={onLogout}
        isPharmacy={isPharmacyRoute}
        isLoggedIn={isPharmacyRoute ? isPharmacyLoggedIn : isProviderLoggedIn}
        onAdminToggle={isPharmacyRoute ? handleAdminToggle : undefined}
        showAdmin={showAdmin}
        onOrdersClick={isPharmacyRoute ? handleOrdersClick : undefined}
        mustChangePassword={mustChangePassword || pharmacistMustChangePassword}
      />
      <Routes>
        <Route path="/login" element={<ProviderLogin onLogin={(token, mustChange) => {
          localStorage.setItem('providerToken', token);
          if (mustChange) localStorage.setItem('mustChangePassword', 'true');
          setIsProviderLoggedIn(true);
          setMustChangePassword(mustChange);
        }} />} />
        <Route path="/request-account" element={<ProviderRequestForm />} />
        <Route path="/pharmacy-login" element={<PharmacyLogin onLogin={(token, mustChange, permLevel) => {
          localStorage.setItem('pharmacistToken', token);
          if (mustChange) localStorage.setItem('pharmacistMustChangePassword', 'true');
          localStorage.setItem('pharmacistPermissionLevel', permLevel);
          setIsPharmacyLoggedIn(true);
          setPharmacistMustChangePassword(mustChange);
          setPharmacistPermissionLevel(permLevel);
        }} />} />
        <Route path="/" element={<ProviderProtectedRoute element={<ListProducts />} />} />
        <Route path="/product/:id" element={<ProviderProtectedRoute element={<ProductPage />} />} />
        <Route path="/checkout/p/:id/s/:strengthId" element={<ProviderProtectedRoute element={<CheckoutPage />} />} />
        <Route path="/history" element={<ProviderProtectedRoute element={<ProviderOrderHistory />} />} />
        <Route path="/history/:id" element={<ProviderProtectedRoute element={<ProviderOrderHistoryDetail />} />} />
        <Route path="/order-confirmed" element={<ProviderProtectedRoute element={<ReorderConfirmation />} />} />
        <Route path="/support" element={<ProviderProtectedRoute element={<ContactSupport />} />} />
        <Route path="/change-password" element={
          isProviderLoggedIn ? (
            <div style={{ maxWidth: 500, margin: '40px auto', textAlign: 'center' }}>
              <h2>Change Your Password</h2>
              <p>You must change your password before continuing.</p>
              <ChangePasswordForm tokenKey="providerToken" onSuccess={() => {
                localStorage.removeItem('mustChangePassword');
                setMustChangePassword(false);
                navigate('/');
              }} />
            </div>
          ) : <Navigate to="/login" replace />
        } />
        <Route path="/pharmacy-change-password" element={
          isPharmacyLoggedIn ? (
            <div style={{ maxWidth: 500, margin: '40px auto', textAlign: 'center' }}>
              <h2>Change Your Password</h2>
              <p>You must change your password before continuing.</p>
              <ChangePasswordForm tokenKey="pharmacistToken" onSuccess={() => {
                localStorage.removeItem('pharmacistMustChangePassword');
                setPharmacistMustChangePassword(false);
                navigate('/pharmacy');
              }} />
            </div>
          ) : <Navigate to="/pharmacy-login" replace />
        } />
        <Route path="/pharmacy-profile" element={
          isPharmacyLoggedIn ? (
            <PharmacyProfile onPasswordChanged={() => navigate('/pharmacy')} />
          ) : <Navigate to="/pharmacy-login" replace />
        } />
        <Route path="/profile" element={<ProviderProtectedRoute element={<TabbedProfile />} />} />
        <Route path="/pharmacy" element={<PharmacyProtectedRoute element={<Prescriptions showAdmin={showAdmin} permissionLevel={pharmacistPermissionLevel} />} />} />
        <Route path="/manage/:id" element={<PharmacyProtectedRoute element={<ManagePage />} />} />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  const [isProviderLoggedIn, setIsProviderLoggedIn] = useState<boolean>(!!localStorage.getItem('providerToken'));
  const [isPharmacyLoggedIn, setIsPharmacyLoggedIn] = useState<boolean>(!!localStorage.getItem('pharmacistToken'));
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(localStorage.getItem('mustChangePassword') === 'true');
  const [pharmacistMustChangePassword, setPharmacistMustChangePassword] = useState<boolean>(localStorage.getItem('pharmacistMustChangePassword') === 'true');
  const [pharmacistPermissionLevel, setPharmacistPermissionLevel] = useState<string>(localStorage.getItem('pharmacistPermissionLevel') || 'full_access');

  const checkAuthStatus = useCallback(() => {
    setIsProviderLoggedIn(!!localStorage.getItem('providerToken'));
    setIsPharmacyLoggedIn(!!localStorage.getItem('pharmacistToken'));
  }, []);

  useEffect(() => {
    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    return () => {
      window.removeEventListener('storage', checkAuthStatus);
    };
  }, [checkAuthStatus]);

  const logout = () => {
    localStorage.removeItem('providerToken');
    localStorage.removeItem('pharmacistToken');
    localStorage.removeItem('mustChangePassword');
    localStorage.removeItem('pharmacistMustChangePassword');
    localStorage.removeItem('pharmacistPermissionLevel');
    setIsProviderLoggedIn(false);
    setIsPharmacyLoggedIn(false);
    setMustChangePassword(false);
    setPharmacistMustChangePassword(false);
    setPharmacistPermissionLevel('full_access');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          <AppContent
            onLogout={logout}
            isProviderLoggedIn={isProviderLoggedIn}
            isPharmacyLoggedIn={isPharmacyLoggedIn}
            mustChangePassword={mustChangePassword}
            pharmacistMustChangePassword={pharmacistMustChangePassword}
            pharmacistPermissionLevel={pharmacistPermissionLevel}
            setIsProviderLoggedIn={setIsProviderLoggedIn}
            setIsPharmacyLoggedIn={setIsPharmacyLoggedIn}
            setMustChangePassword={setMustChangePassword}
            setPharmacistMustChangePassword={setPharmacistMustChangePassword}
            setPharmacistPermissionLevel={setPharmacistPermissionLevel}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;
